import React, { useMemo, useState } from 'react';
import {
  Box,
  ChevronDownIcon,
  ChevronUpIcon,
  EyeIcon,
  formatSnakeCaseToCamelCase,
  HideIcon,
  Inscription,
  RawButton,
  Tip,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { capitalize } from 'lodash';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import Permissions from '@/common/permissions';
import { useHasAccess } from '@/modules/core';

import { Beneficiary } from './useGetBeneficiaries.query';

interface Props {
  beneficiary?: Beneficiary;
}

const BeneficiariesListItem: React.FC<Props> = ({ beneficiary }) => {
  const hasAccess = useHasAccess([{ permission: Permissions.SSN_VIEW }]);
  const [isSsnShown, setIsSsnShown] = useState(false);
  const [isExtended, setIsExtended] = useState(false);
  const formattedAddress = useMemo(() => [
    beneficiary?.address?.line1?.trim(),
    beneficiary?.address?.city?.trim(),
    beneficiary?.address?.state?.trim(),
    beneficiary?.address?.zipcode?.trim(),
  ].filter((item) => item).join(', '), [beneficiary]);
  const formattedSsn = useMemo(() => {
    if (!beneficiary?.ssn) return '';

    if (isSsnShown && hasAccess) return beneficiary.ssn;

    return `••••••••••${beneficiary.ssn.slice(-4)}`;
  }, [beneficiary, hasAccess, isSsnShown]);
  const formattedBirth = useMemo(() => dayjs(beneficiary?.birthOn).format(DEFAULT_DATE_FORMAT), [beneficiary]);

  return (
    <Box
      data-testId={`BeneficiariesListItem_${beneficiary?.id}`}
    >
      <Box
        direction="row"
        onClick={() => setIsExtended(!isExtended)}
      >
        <Box
          data-testId="beneficiary_name"
          width={{ min: '350px' }}
          pad={{ right: 'spacing8' }}
          direction="row"
        >
          <Box margin={{ right: 'spacing24' }}>
            { isExtended ? (
              <ChevronUpIcon color="textTitle" />
            ) : (
              <ChevronDownIcon color="textTitle" />
            )}
          </Box>

          <Tip
            plain
            content={(
              <Box elevation="default" background="canvas" pad="small">
                {beneficiary?.name}
              </Box>
            )}
          >
            <Inscription color="textBody" size="16px" weight="bold" lineHeight="22px">
              {beneficiary?.name}
            </Inscription>
          </Tip>
        </Box>

        <Box data-testId="beneficiary_allocationPercentage" width={{ min: '300px' }} pad={{ right: 'spacing8' }}>
          <Inscription color="textBody" size="16px" lineHeight="22px">
            {beneficiary?.allocationPercentage}%
          </Inscription>
        </Box>

        <Box data-testId="beneficiary_relationshipType" pad={{ right: 'spacing8' }}>
          <Inscription color="textBody" size="16px" lineHeight="22px">
            {capitalize(formatSnakeCaseToCamelCase(beneficiary?.relationshipType as string))}
          </Inscription>
        </Box>
      </Box>

      {isExtended ? (
        <Box
          border={{ side: 'top', size: 'medium', color: 'border2' }}
          pad={{ top: 'spacing12' }}
          margin={{ top: 'spacing12' }}
          direction="row"
        >
          <Box width="43%" pad={{ right: 'spacing24' }}>
            <Inscription size="small" weight="bold" lineHeight="18px" color="textBody">Address</Inscription>
            <Inscription
              truncate
              color="textBody"
              lineHeight="20px"
              data-testId="BeneficiariesListItem_address"
              title={formattedAddress}
            >
              {formattedAddress}
            </Inscription>
          </Box>

          <Box width="30%" pad={{ right: 'spacing24' }}>
            <Inscription size="small" weight="bold" lineHeight="18px" color="textBody">SSN</Inscription>
            <Box direction="row">
              <Inscription
                truncate
                color="textBody"
                lineHeight="20px"
                data-testId="BeneficiariesListItem_SSN"
                title={formattedSsn}
                margin={{ right: 'spacing12' }}
              >
                {formattedSsn}
              </Inscription>
              <RawButton disabled={!hasAccess} onClick={() => setIsSsnShown(!isSsnShown)}>
                {isSsnShown ? <EyeIcon /> : <HideIcon />}
              </RawButton>
            </Box>
          </Box>

          <Box>
            <Inscription size="small" weight="bold" lineHeight="18px" color="textBody">Date of birth</Inscription>
            <Inscription
              truncate
              color="textBody"
              lineHeight="20px"
              data-testId="BeneficiariesListItem_DOB"
              title={formattedBirth}
            >
              {formattedBirth}
            </Inscription>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default BeneficiariesListItem;
